import { doc, getDoc, setDoc } from "firebase/firestore";
import { PAYMENT_CONFIG, STORAGE_KEYS } from "../constants/authConfig";
import {
  db,
  getStoredEmail,
  getStoredTrialEndDate,
  setStoredTrialEndDate,
} from "./authService";
import { getStorageItem, setStorageItem } from "./officeStorageService";

function todayString() {
  return new Date().toISOString().split("T")[0];
}

function addMonthsString(months) {
  const date = new Date();
  date.setMonth(date.getMonth() + months);
  return date.toISOString().split("T")[0];
}

function isSubscriptionActive(endDate) {
  if (!endDate) return false;
  const today = new Date(todayString());
  const subscriptionEnd = new Date(endDate);
  return subscriptionEnd.getTime() > today.getTime();
}

function getPaymentsDocRef() {
  return doc(db, PAYMENT_CONFIG.firestoreDocPath, PAYMENT_CONFIG.firestoreDocId);
}

function getEmailEndDate(userData, email) {
  if (!userData) return null;
  return userData[email] || userData[email.toLowerCase()] || null;
}

function daysUntil(endDate) {
  const today = new Date(todayString());
  const end = new Date(endDate);
  return Math.ceil((end.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

async function isTrialUser(endDate) {
  const trialEndDate = await getStoredTrialEndDate();
  if (!trialEndDate || !endDate) return false;
  return endDate <= trialEndDate;
}

async function rememberTrialEndDateIfNeeded(endDate) {
  const existing = await getStoredTrialEndDate();
  if (!endDate || existing) return;
  if (daysUntil(endDate) <= 31) {
    await setStoredTrialEndDate(endDate);
  }
}

/**
 * Ensure the user exists in Firestore.
 * New users receive a 1-month trial end date.
 */
export async function registerUserInFirebase(email) {
  const normalizedEmail = email.toLowerCase();
  const userDocRef = getPaymentsDocRef();
  const docSnapshot = await getDoc(userDocRef);
  const trialEndDate = addMonthsString(PAYMENT_CONFIG.trialMonths);

  if (docSnapshot.exists()) {
    const userData = docSnapshot.data();
    const existingEndDate = getEmailEndDate(userData, normalizedEmail);

    if (existingEndDate) {
      await rememberTrialEndDateIfNeeded(existingEndDate);
      return { endDate: existingEndDate, isNewUser: false };
    }

    const updatedData = { ...userData, [normalizedEmail]: trialEndDate };
    await setDoc(userDocRef, updatedData);
    await setStoredTrialEndDate(trialEndDate);
    return { endDate: trialEndDate, isNewUser: true };
  }

  const userData = { [normalizedEmail]: trialEndDate };
  await setDoc(userDocRef, userData);
  await setStoredTrialEndDate(trialEndDate);
  return { endDate: trialEndDate, isNewUser: true };
}

export async function getSubscriptionEndDate(email) {
  const userDocRef = getPaymentsDocRef();
  const docSnapshot = await getDoc(userDocRef);

  if (!docSnapshot.exists()) {
    return null;
  }

  return getEmailEndDate(docSnapshot.data(), email);
}

export async function checkSubscriptionStatus(email) {
  const today = todayString();
  const cachedDate = await getStorageItem(STORAGE_KEYS.subscriptionCheckedForDate);
  const cachedStatus = await getStorageItem(STORAGE_KEYS.subscriptionStatus);

  if (cachedDate === today && cachedStatus === "true") {
    const endDate = await getSubscriptionEndDate(email);
    return {
      hasAccess: true,
      endDate,
      isTrial: await isTrialUser(endDate),
      fromCache: true,
    };
  }

  await setStorageItem(STORAGE_KEYS.subscriptionCheckedForDate, today);

  const endDate = await getSubscriptionEndDate(email);

  if (!endDate) {
    await setStorageItem(STORAGE_KEYS.subscriptionStatus, "false");
    return {
      hasAccess: false,
      endDate: null,
      isTrial: false,
      fromCache: false,
    };
  }

  const hasAccess = isSubscriptionActive(endDate);
  await setStorageItem(STORAGE_KEYS.subscriptionStatus, hasAccess ? "true" : "false");

  return {
    hasAccess,
    endDate,
    isTrial: hasAccess && (await isTrialUser(endDate)),
    fromCache: false,
  };
}

export async function ensureParserAccess() {
  const email = await getStoredEmail();
  if (!email) {
    return { allowed: false, reason: "Enter your email to use the XML parser." };
  }

  const { hasAccess, endDate, isTrial } = await checkSubscriptionStatus(email);

  if (!hasAccess) {
    return {
      allowed: false,
      reason: "No active subscription found for this email. Subscribe to continue.",
      email,
      hasAccess: false,
      endDate,
      isTrial: false,
    };
  }

  return {
    allowed: true,
    reason: null,
    email,
    hasAccess: true,
    endDate,
    isTrial,
  };
}

export function openPaymentPage() {
  window.open(PAYMENT_CONFIG.paypalSubscribeUrl, "_blank");
}
